import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

export type NewRelicProps = {
  newRelicBrowserScript: string;

  /**
   * Name of the page you want to use when viewing it in New Relic Browser or Insights. Use alphanumeric characters.
   * Format the name as a slash-delimited string.
   */
  pageViewName?: string;

  /**
   * Default is http://custom.transaction. Typically set host to your site's domain URI.
   * To further group these custom transactions, provide a custom host.
   * Otherwise, the page views will be assigned the default domain custom.transaction.
   * Segments within the name must be explicitly added to your URL allow list settings if they do not already appear.
   */
  pageViewHost?: string;
};

type NewRelicExtensionProps = {
  newRelicFunction: (browserScript: string) => string;
}

declare global {
  interface Window {
    newrelic: any;
  }
}

/**
 * Provides a composed new relic wrapper to hide internal functions.
 */
const NewRelicWrapper = (props: NewRelicProps & NewRelicExtensionProps) => {
  // Run once on new render. If new relic is enabled, window.newrelic.setPageViewName has to be fired once.
  // Make this call before the window load event fires in order for it to appear correctly.
  useEffect(() => {
    if (
      window &&
      window.newrelic &&
      props.pageViewName &&
      props.pageViewName.startsWith('/')
    ) {
      if (props.pageViewHost) {
        window.newrelic.setPageViewName(props.pageViewName, props.pageViewHost);
      } else {
        window.newrelic.setPageViewName(props.pageViewName);
      }
    }
  }, []);

  // script tag is copied from NewRelic for browser instrumentation
  return (
    <Helmet defer={true}>
      <script type="text/javascript">{props.newRelicFunction(props.newRelicBrowserScript)}</script>
    </Helmet>
  );
};

export const NewRelic = (props: NewRelicProps) => (
  <NewRelicWrapper {...props} newRelicFunction={newRelicBrowser} />
);
export default NewRelic;

function newRelicBrowser(browserScript: string) {
  const newRelicBrowserScript = `${browserScript}`;
  return newRelicBrowserScript;
}
